import { Select, Space, Typography } from "antd";
import type { SelectProps } from "antd";
import { Toggle } from "@libertify/ui";
import { useState } from "react";

type ToggleSelectProps = {
  toggleLabel?: string;
  placeholder?: string;
  options?: SelectProps["options"];
  // Injected by the parent Form.Item. Toggle ON means "no value" (use the
  // consumer's default); toggling it off reveals the select.
  value?: string | null;
  onChange?: (value: string | null) => void;
};

/**
 * Controlled toggle + select: ON clears the value (fall back to a default),
 * OFF reveals a select to pick a specific value. Bound by a Form.Item, so
 * value/onChange flow through the form like any other field.
 */
export const ToggleSelect = ({
  toggleLabel,
  placeholder,
  options,
  value,
  onChange,
}: ToggleSelectProps) => {
  // The override lets the user reveal the select before a value exists,
  // falling back to the data meaning (no value = default).
  const [useDefaultOverride, setUseDefaultOverride] = useState<boolean | null>(
    null
  );
  const useDefault = useDefaultOverride ?? value == null;

  const handleToggle = (checked: boolean) => {
    setUseDefaultOverride(checked);
    if (checked && value != null) {
      onChange?.(null);
    }
  };

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Space size={8} style={{ height: 40 }}>
        <Toggle size="l" checked={useDefault} onChange={handleToggle} />
        <Typography.Text strong>{toggleLabel}</Typography.Text>
      </Space>
      {!useDefault && (
        <Select
          value={value ?? undefined}
          onChange={(next) => onChange?.(next ?? null)}
          options={options}
          placeholder={placeholder}
          allowClear
          style={{ width: "100%" }}
        />
      )}
    </Space>
  );
};
